/**
 * @file property-labels.ts
 * @description PropertyType enum 또는 크롤링 한글 문자열을 한글 라벨로 변환하는 유틸리티
 *
 * 주요 기능:
 * 1. PropertyType enum 값을 한글 라벨로 변환
 * 2. 크롤링 데이터의 한글 문자열을 한글 라벨로 변환
 * 3. 모든 PropertyType enum을 커버하는 타입 안전한 매핑
 * 4. SSOT 원칙: 모든 한글 라벨은 이 파일에서만 관리
 *
 * 사용 예시:
 * ```ts
 * import { getPropertyTypeLabel } from "@/lib/domain/auction/utils/property-labels";
 * const label1 = getPropertyTypeLabel(PropertyType.Apartment); // "아파트"
 * const label2 = getPropertyTypeLabel("아파트"); // "아파트"
 * const label3 = getPropertyTypeLabel("다세대"); // "빌라/다세대"
 * ```
 *
 * @dependencies
 * - @/lib/types: PropertyType enum
 * - @/lib/types/crawling: CRAWLING_PROPERTY_TYPE_MAP
 *
 * @see {@link /docs/domain/property-types.md} - PropertyType 명세
 */

import { PropertyType } from "@/lib/core/types";
import { mapCrawlingPropertyType } from "@/lib/core/types/crawling";

/**
 * PropertyType enum을 한글 라벨로 매핑하는 객체
 * 모든 PropertyType enum을 커버해야 함 (타입 안전성 보장)
 */
export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  // 주거용 (기존)
  [PropertyType.Apartment]: "아파트",
  [PropertyType.Villa]: "빌라",
  [PropertyType.Officetel]: "오피스텔",
  [PropertyType.MultiHouse]: "다가구주택",
  [PropertyType.Detached]: "단독주택",
  [PropertyType.ResidentialLand]: "대지(주거)",

  // 주거용 (크롤링 추가)
  [PropertyType.MultiFamily]: "다세대",
  [PropertyType.RowHouse]: "연립주택",
  [PropertyType.MixedUse]: "주상복합",

  // 상업용 (기존)
  [PropertyType.Store]: "상가",
  [PropertyType.Office]: "사무실",
  [PropertyType.Factory]: "공장",
  [PropertyType.Warehouse]: "창고",
  [PropertyType.CommercialLand]: "상업용 토지",

  // 상업용 (크롤링 추가)
  [PropertyType.StoreHouse]: "상가주택",
  [PropertyType.NeighborhoodFacility]: "근린생활시설",
  [PropertyType.RetailFacility]: "판매시설",
  [PropertyType.SportsFacility]: "운동시설",
  [PropertyType.LodgingFacility]: "숙박시설",
};

/**
 * PropertyType enum 값 또는 크롤링 한글 문자열을 한글 라벨로 변환하는 함수
 *
 * @param type - PropertyType enum 값 또는 크롤링 데이터의 한글 문자열 (예: "아파트", "다세대")
 * @returns 한글 라벨 (매핑이 없으면 입력값 그대로 반환)
 *
 * @example
 * ```ts
 * // PropertyType enum 사용
 * getPropertyTypeLabel(PropertyType.Apartment) // "아파트"
 * getPropertyTypeLabel(PropertyType.Villa) // "빌라/다세대"
 *
 * // 크롤링 한글 문자열 사용
 * getPropertyTypeLabel("아파트") // "아파트"
 * getPropertyTypeLabel("다세대") // "빌라/다세대"
 * getPropertyTypeLabel("다가구주택") // "다가구주택"
 * getPropertyTypeLabel("근린생활시설") // "상가"
 * ```
 */
export function getPropertyTypeLabel(type: PropertyType | string): string {
  // PropertyType enum인 경우 직접 매핑
  if (Object.values(PropertyType).includes(type as PropertyType)) {
    return PROPERTY_TYPE_LABELS[type as PropertyType] || type;
  }

  // 크롤링 한글 문자열인 경우 PropertyType으로 변환 후 매핑
  if (typeof type === "string") {
    const propertyType = mapCrawlingPropertyType(type);
    return PROPERTY_TYPE_LABELS[propertyType] || type;
  }

  // 매핑 실패 시 입력값 그대로 반환
  return String(type);
}
